import generateAnchors from '@/lib/generateAnchors';
import generateAnchorsParams from '@/lib/generateAnchorsParams';
import useWindowWidth from '@/hooks/useWindowWidth';
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { animateScroll as scroll } from 'react-scroll';
import { StyledContent } from '@/components/styles';
import { Anchors } from '@/components';
import Swiper from 'react-id-swiper';

export default function AnchoredContent({
  navHeader,
  navFooter,
  contentHeader,
  children = [],
  slider,
  singleLink,
  swiperData,
  arrow,
}) {
  const gallerySwiper = swiperData?.gallerySwiper;
  const thumbnailSwiper = swiperData?.thumbnailSwiper;
  const params = swiperData?.params;
  const thumbnailParams = swiperData?.thumbnailParams;
  const [activeThumb, setActiveThumb] = useState(0);

  const width = useWindowWidth();
  const router = useRouter();

  useEffect(() => {
    if (!gallerySwiper?.controller || !thumbnailSwiper) return;

    const updateSwipers = () => {
      if (!gallerySwiper?.controller || !thumbnailSwiper?.controller) return;
      gallerySwiper.update();
      thumbnailSwiper.update();
      if (typeof gallerySwiper.updateAutoHeight === 'function') {
        gallerySwiper.updateAutoHeight();
      }
    };

    // After client navigation layout often isn't ready yet — resize works because it calls update().
    updateSwipers();
    const raf = requestAnimationFrame(updateSwipers);
    const t0 = setTimeout(updateSwipers, 0);
    const t1 = setTimeout(updateSwipers, 150);
    const t2 = setTimeout(updateSwipers, 500);

    setActiveThumb(gallerySwiper.activeIndex);

      if (gallerySwiper.activeIndex !== 0) {
        if (width > 1024) {
          if (!singleLink) {
            scroll.scrollTo(780, {
              duration: 400,
              smooth: false,
            });
          } else {
            scroll.scrollTo(260, {
              duration: 400,
              smooth: false,
            });
          }
        }
      }
      const onSlideChange = () => {
      setActiveThumb(gallerySwiper.activeIndex);
      thumbnailSwiper.slideTo(gallerySwiper.activeIndex);
    };

    gallerySwiper.on('slideChange', onSlideChange);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t0);
      clearTimeout(t1);
      clearTimeout(t2);
      gallerySwiper.off?.('slideChange', onSlideChange);
    };

  }, [gallerySwiper, thumbnailSwiper, singleLink, width, router.asPath]);

  const noChildren = !children;
  const filteredChildren = children.filter((child) => !!child);
  const onlyOneChild = filteredChildren.length <= 1;
  const onlyOneBlock = filteredChildren.length === 1 && filteredChildren[0].length <= 1;

  if (noChildren || (onlyOneChild && onlyOneBlock && !singleLink)) {
    return (
      <>
        <StyledContent.ColumnSide>
          <StyledContent.Side>
            {navHeader}
            {navFooter}
          </StyledContent.Side>
        </StyledContent.ColumnSide>
        <StyledContent.ColumnMain>
          {contentHeader}
          {children}
        </StyledContent.ColumnMain>
      </>
    );
  } 
  const anchors = generateAnchors(React.Children.map(children, (child) => child && child.props));

  const refs = anchors.reduce((acc, anchor) => {
    acc[anchor.id] = React.createRef();
    return acc;
  }, {});

  const handleClick = (id) => {
    if (slider) {
      setActiveThumb(id);
      gallerySwiper?.slideTo(id);
      thumbnailSwiper?.slideTo(gallerySwiper.activeIndex);
      if (width > 1024) {
        if (!singleLink) {
          scroll.scrollTo(780, {
            duration: 400,
            smooth: false,
          });
        } else {
          scroll.scrollTo(260, {
            duration: 400,
            smooth: false,
          });
        }
      }
    } else {
      refs[id].current.removeAttribute('id');
      scroll.scrollTo(refs[id].current.offsetTop - 90, { smooth: true });
      document.location.hash = id;
      refs[id].current.id = id;
    }
  };

  const reffedChildren = React.Children.map(
    children,
    (child) => child && React.cloneElement(child, { ref: refs[generateAnchorsParams(child.props)[1]] })
  );

  return (
    <>
      <StyledContent.ColumnSide>
        <StyledContent.Side>
          {navHeader}
          <Anchors
            list={anchors}
            handleClick={handleClick}
            slider={slider}
            params={slider ? thumbnailParams : ''}
            activeThumb={activeThumb}
            remountKey={router.asPath}
            arrow={arrow}
          />
          {navFooter}
        </StyledContent.Side>
      </StyledContent.ColumnSide>
      <StyledContent.ColumnMain>
        {slider ? (
          <StyledContent.SliderContent>
            <Swiper key={router.asPath} {...params} shouldSwiperUpdate>
              {reffedChildren}
            </Swiper>
          </StyledContent.SliderContent>
        ) : (
          <>
            {contentHeader}
            {reffedChildren}
          </>
        )}
      </StyledContent.ColumnMain>
    </>
  );
  
}
